/**
 * Сводка дашборда за период (ADMIN_SPEC §11). Живой источник —
 * GET /admin/dashboard/summary?from&to: ключ периода UI переводим
 * в даты (day/week/month или «from..to» из пикера). Мок — как раньше.
 * Суммы админ-зоны — целые сомы (ADMIN_BACKEND_SPEC §0).
 */

import dayjs from 'dayjs';

import {apiEnabled, http} from '@services/utils';

import {DASHBOARD_SUMMARY} from '../mocks';
import type {DashboardSummary} from '../types';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** 'day' | 'week' | 'month' | 'YYYY-MM-DD..YYYY-MM-DD' → границы периода */
const periodRange = (period: string): {from: string; to: string} => {
  const today = dayjs().format('YYYY-MM-DD');
  if (period === 'day') return {from: today, to: today};
  if (period === 'week') return {from: dayjs().subtract(6, 'day').format('YYYY-MM-DD'), to: today};
  if (period === 'month') {
    return {from: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), to: today};
  }
  const [from = today, to = today] = period.split('..');
  return {from, to};
};

const asNum = (v: unknown, fallback = 0): number => (typeof v === 'number' ? v : fallback);
const asObj = (v: unknown): Record<string, unknown> =>
  (typeof v === 'object' && v !== null ? v : {}) as Record<string, unknown>;

const toSummary = (raw: unknown): DashboardSummary => {
  const r = asObj(raw);
  const active = asObj(r.active);
  const mrr = asObj(r.mrr);
  const trial = asObj(r.trial);
  const risks = asObj(r.risks);
  const conversion = asObj(r.conversion);
  const cancelled = asObj(r.cancelled);
  return {
    dateLabel: typeof r.dateLabel === 'string' ? r.dateLabel : '',
    active: {
      total: asNum(active.total),
      solo: asNum(active.solo),
      clinic: asNum(active.clinic),
      deltaPct: asNum(active.deltaPct),
    },
    mrr: {
      value: asNum(mrr.value),
      deltaPct: asNum(mrr.deltaPct),
      deltaAbs: asNum(mrr.deltaAbs),
      spark: (Array.isArray(mrr.spark) ? mrr.spark : []).map((v) => asNum(v)),
    },
    trial: {
      total: asNum(trial.total),
      noCard: asNum(trial.noCard),
      withCard: asNum(trial.withCard),
    },
    risks: {
      trialExpired: asNum(risks.trialExpired),
      awaitingCard: asNum(risks.awaitingCard),
      pastDue: asNum(risks.pastDue),
      blocked: asNum(risks.blocked),
    },
    conversion: {
      pct: asNum(conversion.pct),
      converted: asNum(conversion.converted),
      leads: asNum(conversion.leads),
    },
    cancelled: {
      total: asNum(cancelled.total),
      deltaPct: asNum(cancelled.deltaPct),
      solo: asNum(cancelled.solo),
      clinic: asNum(cancelled.clinic),
    },
  };
};

/** `period` — ключ выбранного периода (день/неделя/месяц/диапазон). */
export const getDashboardSummary = async (period: string): Promise<DashboardSummary> => {
  if (!apiEnabled) {
    await sleep(240);
    return {...DASHBOARD_SUMMARY};
  }

  const raw = await http<unknown>('admin/dashboard/summary', {
    params: periodRange(period),
    skipCaseConversion: true,
  });
  return toSummary(raw);
};
